import { Grid, Typography } from "@mui/material";
import { alpha, Theme } from '@mui/material/styles';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { AppImages } from "assets/appResources";
import { GlobalClassKeys } from "assets/jss/common-styles";
import classnames from "classnames";
import { RequestIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import IconKnowledgeTestEdit from "components/Icons/IconKnowledgeTestEdit";
import IconTrophy from "components/Icons/IconTrophy";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { WithSnackbar, withSnackbar } from "components/SnackbarProvider/Snackbar";
import LibUtils from "lib/LibUtils";
import { KnowledgeTestDifficultyLevel } from "models/ApiEnums";
import { QuizSessionInfoDto, QuizTopicDiscoveryModel } from "models/ApiModels";
import queryString from 'query-string';
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import { withRouter } from "react-router-dom";
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";

/////////////////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({
    container: {
        margin: theme.spacing(2)
    },

    topicBox: {
        flexGrow: 1,
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        borderRadius: "20px",
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: theme.genie.palette.info.main,
        backgroundColor: theme.palette.common.white,
        cursor: "pointer",
        "&:hover": {
            backgroundColor: alpha(theme.genie.palette.info.main, 0.3),
        },
    },

    topicHighlight: {
        backgroundColor: alpha(theme.genie.palette.warning.dark, 0.1),
        borderColor: theme.genie.palette.warning.dark,
        "& $topicTitle": {
            color: theme.genie.palette.warning.main,
        }
    },

    topicTitle: {
        color: theme.genie.palette.info.main,
        fontWeight: "bolder",
        marginBottom: 4
    }
});

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    data: QuizTopicDiscoveryModel[];
    openSession: QuizSessionInfoDto;
    loading: boolean;
    lastError: any;
}

interface IProps {
}

type Props = IProps & WithStyles<typeof styles> & WithSnackbar & WithTranslation & RouteComponentProps;

/////////////////////////////////////////////////////////////////////////////////////////////////

export class QuizSelection extends React.Component<Props, IState> {

    private topicHighlight: string = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            openSession: null,
            loading: true,
            lastError: null,
        };

        const q = queryString.parse(document.location.search);

        // o parâmetro "fn" é colocado na query string por 'NotificationMessage'.
        this.topicHighlight = q["thl"] as string;
    }

    ///
    private loadBreakSession() {
        api.quizGetBreakSession(Session.user.id)
            .then((result) => {
                if (result == null)
                    this.loadData();
                else
                    this.setState({ openSession: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, lastError: error, loading: false });
            });
    }

    ///
    private loadData() {
        api.quizDiscoveryTopics(Session.user.id)
            .then((result) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, lastError: error, loading: false });
            });
    }

    ///
    private handleSelection(selection: QuizTopicDiscoveryModel) {
        api.quizCreateSession(Session.user.id, selection)
            .then((sessionId) => {
                this.props.history.push(RoutePaths.QuizExecute.replace(":id", sessionId));
            })
            .catch((error) => {
                this.setState({ data: null, lastError: error, loading: false });
            });
    }

    ///
    private renderTopics() {
        const { data } = this.state;

        return data.map((v, index) => {

            let level = "";
            if (v.revision)
                level = this.props.t("Views.Quiz.QuizSelection.RevisionLevel");
            else if (v.answeredTests > 0 && v.newestCounter > 0 && v.newestMinDifficultyLevel < v.userCurrentLevel)
                level = this.props.t("Views.Quiz.QuizSelection.CompleteTestsLevel");
            else if (v.sequential)
                level = this.props.t(`sys:KnowledgeTestDifficultyLevel.${KnowledgeTestDifficultyLevel[v.newestCounter > 0 ? v.newestMinDifficultyLevel : v.wrongMinDifficultyLevel]}`);
            else
                level = this.props.t(`sys:KnowledgeTestDifficultyLevel.${KnowledgeTestDifficultyLevel[v.userCurrentLevel]}`);

            return (
                <div key={index} onClick={() => this.handleSelection(v)}>
                    <Grid container direction="column" wrap="nowrap"
                        className={
                            classnames(
                                this.props.classes.topicBox,
                                {
                                    [this.props.classes.topicHighlight]: v.topicId == this.topicHighlight
                                }
                            )
                        }
                    >
                        <Grid item>
                            <div className={this.props.classes.topicTitle}>{v.name}</div>
                        </Grid>
                        <Grid item>
                            <Grid container>
                                <Grid item xs={4}>
                                    <MuiMuted><b>{level}</b></MuiMuted>
                                </Grid>
                                <Grid item xs={4} className={GlobalClassKeys.textCenter}>
                                    <MuiMuted title={this.props.t("Views.Quiz.QuizSelection.TooltipTestsToComplete")} >
                                        <IconKnowledgeTestEdit fixedWidth />&nbsp;{v.computedTestsToAnswer}
                                    </MuiMuted>
                                </Grid>
                                <Grid item xs={4} className={GlobalClassKeys.textCenter}>
                                    <MuiMuted title={this.props.t("Views.Quiz.QuizSelection.TooltipPointsToEarn")}>
                                        <IconTrophy fixedWidth />&nbsp;{v.computedAwardPoints}
                                    </MuiMuted>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            );
        });
    }

    ///
    private renderOpenSession() {
        const { openSession: { topicName, topicThumbnailUrl } } = this.state;

        var firstName = LibUtils.firstWord(Session.user.name);

        return (
            <Grid container justifyContent="space-between" spacing={3} >
                <Grid item xs>

                    <MuiContainer margin="large">
                        <Typography component="p" variant="h5">
                            <b>
                                <span>{firstName}</span>
                                <span>, {this.props.t("Views.Quiz.QuizSelection.SessionNotFinalized")}</span>
                            </b>
                        </Typography>
                    </MuiContainer>

                    <Grid container justifyContent="space-between" spacing={2}>
                        {
                            topicThumbnailUrl &&
                            <Grid item>
                                <img src={topicThumbnailUrl} alt={topicName} />
                            </Grid>
                        }
                        <Grid item xs>

                            <MuiContainer>
                                <Typography variant="h6">
                                    <b>{topicName}</b>
                                </Typography>
                            </MuiContainer>

                            <Typography variant="subtitle1">{this.props.t("Common.Question.WhatToDo")}</Typography>
                            <Grid container>
                                <Grid item>
                                    <MuiRegularButton
                                        color="info"
                                        onClick={
                                            () => {
                                                this.props.history.push(RoutePaths.QuizExecute.replace(":id", this.state.openSession.sessionId));
                                            }
                                        }
                                    >
                                        <span>{this.props.t("Views.Quiz.QuizSelection.FinalizeSession")}</span>
                                    </MuiRegularButton>

                                    <MuiRegularButton
                                        color="info"
                                        onClick={
                                            () => {
                                                this.setState({ openSession: null, loading: true }, () => this.loadData());
                                            }
                                        }
                                    >
                                        <span>{this.props.t("Views.Quiz.QuizSelection.SelectAnotherTopic")}</span>
                                    </MuiRegularButton>
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>

                </Grid>

                <Grid item>
                    <img src={AppImages.TheGenieLeft} alt="Genie Logo" />
                </Grid>
            </Grid>
        );
    }

    ///
    private renderList() {
        const { data } = this.state;

        var firstName = LibUtils.firstWord(Session.user.name);

        return (
            <Grid container justifyContent="space-between" alignItems="center" spacing={3} >
                <Grid item xs>

                    <MuiContainer>
                        {
                            data.length == 0
                                ? (
                                    <Typography variant="h5">
                                        <span>{this.props.t("Views.Quiz.QuizSelection.AllDone")}</span>
                                        <br /><br />
                                        <span>{this.props.t("Views.Quiz.QuizSelection.AllDone2")}</span>
                                        <br /><br />
                                        <span>{this.props.t("Views.Quiz.QuizSelection.AllDone3")}</span>
                                    </Typography>
                                )
                                : (
                                    <Typography variant="h5">
                                        <b>
                                            <span>{firstName}</span>
                                            <span>, {this.props.t("Views.Quiz.QuizSelection.Subtitle1")}</span>
                                        </b>
                                    </Typography>
                                )
                        }
                    </MuiContainer>

                    {this.renderTopics()}
                </Grid>
                <Grid item>
                    <img src={AppImages.TheGenieLeft} alt="Genie Logo" />
                </Grid>
            </Grid >
        );
    }

    ///
    public componentDidMount() {
        this.loadBreakSession();
    }

    ///
    public render() {
        const { openSession, lastError, loading } = this.state;

        if (lastError)
            return <RequestErrorReport error={lastError} />;

        if (loading)
            return <RequestIndicator type="request" />;


        return (
            <div className={this.props.classes.container}>
                {
                    openSession ? this.renderOpenSession() : this.renderList()
                }
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

export default withRouter(
    withSnackbar(
        withTranslation(["clientApp", "sys"])(
            withStyles(styles)(
                QuizSelection
            )
        )
    )
);
